<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useQuery, keepPreviousData } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import { Roles } from '@/constants.ts'
import emailHttpService, { type EmailMessageRow } from '@/services/http/emailHttpService.ts'
import EmailDetailDialog from '@/components/EmailDetailDialog.vue'
import EmailContact from '@/pages/registrations/EmailContact.vue'

definePage({
  meta: {
    allowedRoles: [Roles.Admin, Roles.Manager],
  },
})

const { t } = useI18n()

// --- Фильтры ---
const messageTypeCode = ref<string | null>(null)
const eventTypeCode = ref<string | null>(null)
// OTP показываем по умолчанию; тоггл позволяет скрыть письма с кодом.
const includeOtp = ref(true)
const email = ref('')
const dateRange = ref<Date[] | null>(null)

const emailDebounced = ref('')
let emailTimer: ReturnType<typeof setTimeout> | undefined
watch(email, (value) => {
  clearTimeout(emailTimer)
  emailTimer = setTimeout(() => {
    emailDebounced.value = value
  }, 300)
})

// Назначение письма (purpose): приглашение / код подтверждения (общий MessageType, как у SMS).
const typeOptions = computed(() => [
  { label: t('sms.types.Invitation'), value: 'Invitation' },
  { label: t('sms.types.OtpCode'), value: 'OtpCode' },
])

// Опции фильтра по событию — подписи из справочника БД (без дублирования в i18n);
// Pending — синтетическое состояние «ещё не отправлено», в справочнике его нет, добавляем вручную.
const eventTypesQuery = useQuery({
  queryKey: ['admin', 'email', 'event-types'],
  queryFn: () => emailHttpService.eventTypes(),
  staleTime: Infinity,
})

const eventStatusOptions = computed(() => [
  { label: t('email.eventStatuses.Pending'), value: 'Pending' },
  ...(eventTypesQuery.data.value ?? []).map((et) => ({
    label: et.name ?? et.code,
    value: et.code,
  })),
])

// --- Пагинация ---
const page = ref(1)
const pageSize = ref(50)
const first = computed(() => (page.value - 1) * pageSize.value)

interface PageEvent {
  page: number
  rows: number
}

function onPage(event: PageEvent) {
  page.value = event.page + 1
  pageSize.value = event.rows
}

function toIsoDate(d: Date | undefined | null): string | undefined {
  if (!d) return undefined
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString()
}

watch([messageTypeCode, eventTypeCode, includeOtp, emailDebounced, dateRange], () => {
  page.value = 1
})

const listQuery = useQuery({
  queryKey: computed(() => [
    'admin',
    'email',
    'list',
    page.value,
    pageSize.value,
    messageTypeCode.value,
    eventTypeCode.value,
    includeOtp.value,
    emailDebounced.value,
    dateRange.value,
  ]),
  queryFn: () =>
    emailHttpService.list({
      page: page.value,
      pageSize: pageSize.value,
      messageTypeCode: messageTypeCode.value,
      eventTypeCode: eventTypeCode.value,
      includeOtp: includeOtp.value,
      email: emailDebounced.value.trim() || undefined,
      from: toIsoDate(dateRange.value?.[0]),
      to: toIsoDate(dateRange.value?.[1]),
    }),
  placeholderData: keepPreviousData,
})

const rows = computed<EmailMessageRow[]>(() => listQuery.data.value?.items ?? [])
const total = computed(() => listQuery.data.value?.total ?? 0)

const selectedId = ref<string | null>(null)
const detailVisible = ref(false)
function openMessage(id: string) {
  selectedId.value = id
  detailVisible.value = true
}

function formatDateTime(value: string | null): string {
  if (value == null) return '—'
  const date = new Date(value)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

type TagSeverity = 'success' | 'warn' | 'danger' | 'secondary' | 'info'

// Цвет тега по коду события: Delivered — зелёный; Sent/Opened/Clicked — синий;
// Bounced/SpamComplaint/Failed — красный; иначе — серый.
function severityForCode(code: string | null): TagSeverity {
  switch (code) {
    case 'Delivered':
      return 'success'
    case 'Opened':
    case 'Clicked':
    case 'Sent':
      return 'info'
    case 'Bounced':
    case 'SpamComplaint':
    case 'Failed':
      return 'danger'
    default:
      return 'secondary'
  }
}

// Подпись статуса доставки — из справочника (DB *Name), не из i18n. Итог доставки (lastEvent)
// важнее статуса отправки; Pending — синтетическое состояние без события (метка из i18n).
function currentStatusLabel(row: EmailMessageRow): string {
  return row.lastEventName ?? row.sendEventName ?? t('email.eventStatuses.Pending')
}

function currentStatusSeverity(row: EmailMessageRow): TagSeverity {
  return severityForCode(row.lastEventCode ?? row.sendEventCode)
}

function typeLabel(row: EmailMessageRow): string {
  const key = `sms.types.${row.messageTypeCode}`
  const translated = t(key)
  const base = translated === key ? (row.messageTypeName ?? row.messageTypeCode) : translated
  return row.invitationTypeName ? `${base} · ${row.invitationTypeName}` : base
}
</script>

<template>
  <section class="flex flex-col gap-4 w-full">
    <h1 class="text-xl font-bold">{{ t('email.title') }}</h1>

    <!-- Фильтры -->
    <div class="flex flex-wrap items-end gap-3">
      <div class="flex flex-col gap-1">
        <label for="emailType" class="text-sm">{{ t('email.filters.messageType') }}</label>
        <Select
          id="emailType"
          v-model="messageTypeCode"
          class="w-56"
          :options="typeOptions"
          option-label="label"
          option-value="value"
          show-clear
          :placeholder="t('email.filters.all')"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label for="emailStatus" class="text-sm">{{ t('email.filters.eventStatus') }}</label>
        <Select
          id="emailStatus"
          v-model="eventTypeCode"
          class="w-56"
          :options="eventStatusOptions"
          option-label="label"
          option-value="value"
          show-clear
          :placeholder="t('email.filters.all')"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label for="emailSearch" class="text-sm">{{ t('email.filters.email') }}</label>
        <InputText
          id="emailSearch"
          v-model="email"
          class="w-56"
          :placeholder="t('email.filters.emailPlaceholder')"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label for="emailDates" class="text-sm">{{ t('email.filters.dateRange') }}</label>
        <DatePicker
          id="emailDates"
          v-model="dateRange"
          class="w-64"
          selection-mode="range"
          :manual-input="false"
          show-button-bar
          date-format="dd.mm.yy"
          :placeholder="t('email.filters.dateRangePlaceholder')"
        />
      </div>
      <div class="flex items-center gap-2 pb-2">
        <ToggleSwitch input-id="emailIncludeOtp" v-model="includeOtp" />
        <label for="emailIncludeOtp" class="text-sm">{{ t('email.filters.includeOtp') }}</label>
      </div>
    </div>

    <DataTable
      :value="rows"
      lazy
      paginator
      :rows="pageSize"
      :first="first"
      :total-records="total"
      :rows-per-page-options="[20, 50, 100]"
      :loading="listQuery.isLoading.value"
      data-key="id"
      class="w-full"
      @page="onPage"
    >
      <template #empty>
        <div class="text-sm text-surface-500 py-4">{{ t('email.empty') }}</div>
      </template>

      <Column :header="t('email.columns.messageId')">
        <template #body="{ data }">
          <button
            type="button"
            class="text-sm text-primary text-left bg-transparent border-0 p-0 cursor-pointer break-all"
            @click="openMessage(data.id)"
          >
            {{ data.providerMessageId ?? '—' }}
          </button>
        </template>
      </Column>
      <Column :header="t('email.columns.type')">
        <template #body="{ data }">{{ typeLabel(data) }}</template>
      </Column>
      <Column :header="t('email.columns.email')">
        <template #body="{ data }"><EmailContact :email="data.email" /></template>
      </Column>
      <Column :header="t('email.columns.status')">
        <template #body="{ data }">
          <Tag :value="currentStatusLabel(data)" :severity="currentStatusSeverity(data)" />
        </template>
      </Column>
      <Column :header="t('email.columns.createdAt')">
        <template #body="{ data }">
          <span class="text-sm">{{ formatDateTime(data.createdAt) }}</span>
        </template>
      </Column>
      <Column :header="t('email.columns.sentAt')">
        <template #body="{ data }">
          <span v-if="data.sentAt" class="text-sm">{{ formatDateTime(data.sentAt) }}</span>
          <span v-else class="text-surface-400">—</span>
        </template>
      </Column>
      <Column :header="t('email.columns.deliveredAt')">
        <template #body="{ data }">
          <span v-if="data.lastEventCode === 'Delivered' && data.lastEventAt" class="text-sm">
            {{ formatDateTime(data.lastEventAt) }}
          </span>
          <span v-else class="text-surface-400">—</span>
        </template>
      </Column>
      <Column :header="t('email.columns.lastStatus')">
        <template #body="{ data }">
          <Tag
            v-if="data.latestEventCode"
            :value="data.latestEventName ?? data.latestEventCode"
            :severity="severityForCode(data.latestEventCode)"
          />
          <span v-else class="text-surface-400">—</span>
        </template>
      </Column>
      <Column :header="t('email.columns.lastStatusAt')">
        <template #body="{ data }">
          <span v-if="data.latestEventAt" class="text-sm">{{
            formatDateTime(data.latestEventAt)
          }}</span>
          <span v-else class="text-surface-400">—</span>
        </template>
      </Column>
    </DataTable>

    <EmailDetailDialog v-model:visible="detailVisible" :id="selectedId" />
  </section>
</template>
